import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, Checkbox, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, AttachmentList, attachmentLabel, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
import { asDataTableRows } from '../../../data-table.js';
import { addMonths, parseDateValue, parseNumberValue, postSigningRule, subtractMonths, } from '../../../notification-rules.js';
/**
 * Notification rules (R5). A block lease is fixed-term and auto-extends
 * under §7 unless a party gives notice. We remind ahead of the initial
 * end date and each rolling renewal point (mirrors lokalhyra's logic),
 * with the förlängningstid expressed as years + months.
 */
const BLOCKHYRA_RECURRING_MAX_CYCLES = 5;
const blockhyraNotificationRules = [
    {
        ruleKey: 'termination_notice',
        kind: 'termination_notice',
        label: 'Uppsägning',
        phase: 'post_signing',
        resolve: (ctx) => {
            const leaseEnd = parseDateValue(ctx.values.lease_end_date);
            const notice = parseNumberValue(ctx.values.notice_months);
            if (!leaseEnd || notice === null || notice <= 0) {
                return [];
            }
            const extYears = parseNumberValue(ctx.values.extension_years) ?? 0;
            const extMonths = parseNumberValue(ctx.values.extension_months) ?? 0;
            const Y = extYears * 12 + extMonths;
            const hasExtension = Y > 0;
            const cycles = hasExtension ? BLOCKHYRA_RECURRING_MAX_CYCLES : 1;
            const occurrences = [];
            for (let i = 1; i <= cycles; i++) {
                const cycleEnd = addMonths(leaseEnd, (i - 1) * Y);
                const fireAt = subtractMonths(cycleEnd, notice);
                occurrences.push({
                    triggerAt: cycleEnd,
                    fireAt,
                    leadLabel: 'custom',
                    label: i === 1
                        ? `${notice} månader före hyresavtalet löper ut`
                        : `${notice} månader före automatisk förlängning nr ${i - 1} löper ut`,
                });
            }
            return occurrences;
        },
    },
    postSigningRule({
        ruleKey: 'lease_end',
        kind: 'lease_end',
        label: 'Hyresavtalet löper ut',
        resolveTriggerAt: (values) => parseDateValue(values.lease_end_date),
    }),
];
/**
 * Blocklägenhetens enheter (§4). At least three dwellings. The
 * "Undandras förhandlingsordning" column marks whether the unit is
 * withdrawn from the förhandlingsordning that applies to the building
 * (a per-row checkbox in the Word form — stored Ja/Nej, rendered ☑/☐).
 */
const BLOCK_UNIT_COLUMNS = [
    { key: 'objektsnummer', label: 'Objektsnummer', width: '6rem' },
    { key: 'adress', label: 'Adress', width: '12rem' },
    { key: 'utrymme', label: 'Ev. tillhörande utrymme', width: '9rem' },
    {
        key: 'undandras',
        label: 'Förhandlingsordningens tillämplighet',
        width: '8rem',
        description: 'Ja om lägenheten ska undandras husets förhandlingsordning, annars Nej.',
    },
];
/**
 * §21 Övriga bestämmelser — additional appendices that form part of the
 * agreement. One row per bilaga: its number/label and what it concerns.
 */
const OTHER_ANNEX_COLUMNS = [
    { key: 'bilaga', label: 'Bilaga', width: '6rem' },
    { key: 'avseende', label: 'Avseende', width: '16rem' },
];
export const metadata = {
    templateKey: 'blockhyra',
    version: 'v1',
    category: 'Blockhyra',
    title: 'Blockhyresavtal för företagsbostäder · Version 2026',
    shortTitle: 'Blockhyresavtal',
    formNumber: 'Formulär R-26',
    preamble: 'Undertecknande har denna dag träffat följande blockhyresavtal för företagsbostadsändamål.',
    description: 'Blockhyresavtal för uthyrning av minst tre lägenheter (företagsbostäder) till en blockhyresgäst som i sin tur upplåter dem i andra hand — antingen till sina egna anställda eller en avgränsad krets (Punkt 3a), eller till en juridisk person som erbjuder bostad åt sin personal (Punkt 3b).\n\n' +
        'Hyran anges per år. Besittningsskyddet följer lokalhyresreglerna (indirekt) eller hyreslagens regler om direkt besittningsskydd. Avtalet kan kräva hyresnämndens tillstånd enligt 12 kap. 1 e § jordabalken.\n\n' +
        'Undertecknas av hyresvärd och blockhyresgäst.',
    status: 'draft',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
        },
        {
            id: 'lessee',
            label: 'Blockhyresgäst',
            signingLabel: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
        },
    ],
    // Party projection (ADR-0065) — the blockhyresgäst is always an org.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'landlord_name',
                    requiredAtFinal: true,
                    help: 'Fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address' },
                { attr: 'phone', key: 'landlord_phone' },
                { attr: 'email', key: 'landlord_email' },
            ],
        },
        {
            slot: 'lessee',
            section: 'blockhyresgast',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'tenant_name',
                    requiredAtFinal: true,
                    help: 'Fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'tenant_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'tenant_address', label: 'Aviseringsadress' },
                { attr: 'phone', key: 'tenant_phone' },
                { attr: 'email', key: 'tenant_email' },
            ],
        },
    ],
    // Document sections in fixed order (per ADR-0049). §1–20 match the Word
    // form's numbered list; all always render, so numbering is stable 1–20.
    // Bilagor + Underskrift are unnumbered in the source and render as
    // trailing blocks outside the numbered set.
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'blockhyresgast', title: 'Blockhyresgäst' },
        { ref: 'upplatelseandamal', title: 'Upplåtelseändamål: företagsbostäder' },
        { ref: 'omfattning', title: 'Blocklägenhetens omfattning' },
        { ref: 'skick', title: 'Blockhyreslägenhetens skick' },
        { ref: 'hyrestid', title: 'Hyrestid' },
        { ref: 'uppsagning', title: 'Uppsägning' },
        { ref: 'hyra', title: 'Hyra' },
        {
            ref: 'indexklausul',
            title: 'Indexklausul',
            visibleWhen: { index_clause_included: true },
        },
        { ref: 'betalning', title: 'Hyrans betalning' },
        { ref: 'ranta', title: 'Ränta, betalningspåminnelse' },
        { ref: 'reservnycklar', title: 'Reservnycklar' },
        { ref: 'underhall', title: 'Underhåll m m' },
        {
            ref: 'besittningsskydd',
            title: 'Besittningsskydd för blockhyresgästen',
            visibleWhen: { possession_protection_indirect: true },
        },
        // §9, §14–17, §19–20 are single-checkbox provisions: each self-hides (and the
        // rest renumber) when its toggle is off, and renders as plain text — no
        // checkbox — when on. `mode='blank'` shows them all for the gallery.
        {
            ref: 'forutsattning',
            title: 'Förutsättning för avtalets giltighet',
            visibleWhen: { requires_tribunal_approval: true },
        },
        {
            ref: 'ansokan',
            title: 'Ansökan om hyresnämndens tillstånd',
            visibleWhen: { tenant_applies_approval: true },
        },
        {
            ref: 'sarskild-anpassning',
            title: 'Särskild anpassning',
            visibleWhen: { special_adaptation: true },
        },
        { ref: 'allmanna-villkor', title: 'Allmänna villkor' },
        {
            ref: 'inskrivning',
            title: 'Inskrivning',
            visibleWhen: { no_inscription: true },
        },
        {
            ref: 'personuppgifter',
            title: 'Hantering av personuppgifter',
            visibleWhen: { pdpa_info_included: true },
        },
        { ref: 'ovriga-bestammelser', title: 'Övriga bestämmelser' },
    ],
    // Editor field-panel grouping. Party sections (hyresvard/blockhyresgast)
    // are realised by the dedicated PartiesPanel — not listed here. Field-less
    // sections (ranta, allmanna-villkor) produce no panel. Everything else is
    // grouped into a few panels below.
    inputSections: [
        {
            id: 'upplatelse-objekt',
            label: 'Upplåtelse & objekt',
            sectionRefs: ['upplatelseandamal', 'omfattning', 'skick'],
        },
        {
            id: 'hyrestid-uppsagning',
            label: 'Hyrestid & uppsägning',
            sectionRefs: ['hyrestid', 'uppsagning'],
        },
        {
            id: 'hyra-avgifter',
            label: 'Hyra & avgifter',
            sectionRefs: ['hyra', 'indexklausul', 'betalning'],
        },
        {
            id: 'villkor',
            label: 'Övriga villkor',
            sectionRefs: [
                'reservnycklar',
                'underhall',
                'besittningsskydd',
                'forutsattning',
                'ansokan',
                'sarskild-anpassning',
                'inskrivning',
                'personuppgifter',
                'ovriga-bestammelser',
            ],
        },
    ],
    notificationRules: blockhyraNotificationRules,
};
// Editor-facing select labels keep the "Punkt 3.a/3.b" prefix so the author
// knows which ground they're picking; the document renders only the body text
// (PURPOSE_GROUND_*_BODY), without the prefix or a checkbox.
const PURPOSE_GROUND_3A = 'Punkt 3.a – Hyresgästen upplåter lägenhetsenheterna till sina egna anställda eller en annan tydligt avgränsad krets av personer.';
const PURPOSE_GROUND_3B = 'Punkt 3.b – Blockhyresgästen (som är mellanhand) upplåter lägenhetsenheterna till en juridisk person som i sin tur erbjuder bostad åt sin personal (anställda, uppdragstagare, konsulter).';
const PURPOSE_GROUND_3A_BODY = 'Hyresgästen upplåter lägenhetsenheterna till sina egna anställda eller en annan tydligt avgränsad krets av personer.';
const PURPOSE_GROUND_3B_BODY = 'Blockhyresgästen (som är mellanhand) upplåter lägenhetsenheterna till en juridisk person som i sin tur erbjuder bostad åt sin personal (anställda, uppdragstagare, konsulter).';
const OBS_3A = 'Obs! Kretsen måste ha en koppling till hyresgästens verksamhet eller vara begränsad på annat definierat sätt. Upplåtelse till obestämd allmänhet uppfyller inte kravet.';
const OBS_3B = 'Obs! Blockhyresgästen får inte upplåta direkt till slutanvändarnas anställda eller uppdragstagare. Upplåtelsen i andrahandsledet måste ske till den juridiska personen, som ska erbjuda bostad åt sin personal.';
export const variableSchema = [
    // Avtalsuppgifter
    {
        key: 'contract_number',
        label: 'Kontraktsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        requiredAtFinal: true,
        help: 'Egen referens på avtalet, t.ex. A-2042.',
        scopes: { copy: 'never' },
    },
    {
        key: 'effective_date',
        label: 'Avtalets ikraftträdande',
        type: 'date',
        section: 'Avtalsuppgifter',
        help: 'Det datum avtalet träder i kraft.',
        scopes: { copy: 'never' },
    },
    // §1 Hyresvärd / §2 Blockhyresgäst — generated from
    // metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // §3 Upplåtelseändamål
    {
        key: 'purpose_ground',
        label: 'Tillämplig grund',
        type: 'select',
        section: 'upplatelseandamal',
        requiredAtFinal: true,
        help: 'Ange tillämplig grund (endast ett alternativ).',
        options: [
            { value: 'punkt_3a', label: PURPOSE_GROUND_3A, code: 'punkt_3a', summary: 'Ground 3a: tenant sublets to its own employees or a clearly defined circle of persons.' },
            { value: 'punkt_3b', label: PURPOSE_GROUND_3B, code: 'punkt_3b', summary: 'Ground 3b: block tenant (intermediary) sublets to a legal person that in turn houses its personnel.' },
        ],
    },
    {
        key: 'circle_type',
        label: 'Kretsen utgörs av',
        type: 'select',
        section: 'upplatelseandamal',
        visibleWhen: { purpose_ground: 'punkt_3a' },
        options: [
            { value: 'egna_anstallda', label: 'Egna anställda', code: 'egna_anstallda', summary: 'The circle consists of the tenant’s own employees.' },
            { value: 'annan_krets', label: 'Annan krets, specificerad nedan', code: 'annan_krets', summary: 'The circle is another defined group, specified in free text.' },
        ],
    },
    {
        key: 'circle_specification',
        label: 'Avgränsningskriterium',
        type: 'textarea',
        section: 'upplatelseandamal',
        visibleWhen: { purpose_ground: 'punkt_3a', circle_type: 'annan_krets' },
        help: 'T.ex. anställning hos, uppdrag för, tillhörighet till. Kretsen måste ha en koppling till hyresgästens verksamhet eller vara begränsad på annat definierat sätt.',
    },
    // Editor-only guidance (type: 'note') — never rendered in the document.
    {
        key: 'obs_3a',
        label: 'Obs – avgränsad krets',
        type: 'note',
        section: 'upplatelseandamal',
        visibleWhen: { purpose_ground: 'punkt_3a' },
        help: OBS_3A,
    },
    {
        key: 'obs_3b',
        label: 'Obs – upplåtelse via juridisk person',
        type: 'note',
        section: 'upplatelseandamal',
        visibleWhen: { purpose_ground: 'punkt_3b' },
        help: OBS_3B,
    },
    // §4 Blocklägenhetens omfattning
    {
        key: 'block_scope_mode',
        label: 'Hur anges blocklägenhetens omfattning',
        type: 'select',
        section: 'omfattning',
        requiredAtFinal: true,
        default: 'inline_table',
        options: [
            { value: 'inline_table', label: 'Ange enheterna i tabellen nedan', code: 'inline_table', summary: 'The included units are listed inline in the contract table.' },
            { value: 'per_annex', label: 'Framgår av bilaga', code: 'per_annex', summary: 'The included units are described in a separate annex.' },
        ],
    },
    {
        key: 'block_units',
        label: 'Enheter i blocklägenheten (minst tre)',
        type: 'data_table',
        section: 'omfattning',
        columns: BLOCK_UNIT_COLUMNS,
        minRows: 3,
        visibleWhen: { block_scope_mode: 'inline_table' },
        help: 'Lista de lägenheter som ingår i blocklägenheten. Minst tre enheter krävs.',
    },
    {
        key: 'block_scope_annex',
        label: 'Bilaga – blocklägenhetens omfattning',
        type: 'attachment_ref',
        section: 'omfattning',
        requiredAtFinal: true,
        attachmentDefaultName: 'Blocklägenhetens omfattning',
        visibleWhen: { block_scope_mode: 'per_annex' },
        help: 'Välj eller ladda upp bilagan som anger blocklägenhetens omfattning.',
    },
    // §5 Blockhyreslägenhetens skick
    {
        key: 'condition',
        label: 'Blockhyreslägenhetens skick',
        type: 'select',
        section: 'skick',
        requiredAtFinal: true,
        default: 'befintligt',
        options: [
            { value: 'befintligt', label: 'Befintligt skick', code: 'befintligt', summary: 'The block apartment is let in its existing condition.' },
            { value: 'avtalat_enligt_bilaga', label: 'Avtalat skick enligt bilaga', code: 'avtalat_enligt_bilaga', summary: 'The condition is agreed and specified in an annex.' },
        ],
    },
    {
        key: 'condition_annex',
        label: 'Bilaga – avtalat skick',
        type: 'attachment_ref',
        section: 'skick',
        requiredAtFinal: true,
        attachmentDefaultName: 'Avtalat skick',
        visibleWhen: { condition: 'avtalat_enligt_bilaga' },
    },
    // §6 Hyrestid
    {
        key: 'lease_start_date',
        label: 'Från och med',
        type: 'date',
        section: 'hyrestid',
        requiredAtFinal: true,
        scopes: { imported: { from: 'dates.start', requiredForReview: true }, copy: 'manual' },
    },
    {
        key: 'lease_end_date',
        label: 'Till och med',
        type: 'date',
        section: 'hyrestid',
        durationFrom: 'lease_start_date',
        requiredAtFinal: true,
        scopes: { imported: { from: 'dates.end' }, copy: 'manual' },
    },
    // §7 Uppsägning
    {
        key: 'notice_months',
        label: 'Uppsägningstid (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        requiredAtFinal: true,
        help: 'Uppsägning ska ske skriftligen minst så här många månader före hyrestidens utgång.',
        scopes: { imported: { from: 'dates.notice_period_months' } },
    },
    {
        key: 'extension_years',
        label: 'Förlängning (år)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        help: 'Om avtalet inte sägs upp förlängs det med detta antal år för varje gång.',
    },
    {
        key: 'extension_months',
        label: 'Förlängning (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        help: 'Alternativt antal månader förlängningen avser för varje gång.',
    },
    // §8 Hyra
    {
        key: 'rent_yearly',
        label: 'Årshyra (kr/år)',
        type: 'currency',
        min: 0,
        section: 'hyra',
        requiredAtFinal: true,
        scopes: { imported: { from: 'rent.yearly_sek', requiredForReview: true }, copy: 'manual' },
    },
    {
        key: 'rent_type',
        label: 'Hyran utgör',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'total',
        options: [
            { value: 'total', label: 'Total hyra', code: 'total', summary: 'The stated yearly amount is the total rent.' },
            { value: 'exkl_tillagg', label: 'Hyra exkl. nedan markerade tillägg', code: 'exkl_tillagg', summary: 'The stated rent excludes the supplements marked below.' },
        ],
    },
    {
        key: 'heating',
        label: 'Kostnad för värme',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Heating cost is included in the rent.' },
            { value: 'tillagg_bilaga', label: 'Värmetillägg utgår enligt bifogad klausul', code: 'tillagg_bilaga', summary: 'A heating supplement applies per an attached clause/annex.' },
        ],
    },
    {
        key: 'heating_annex',
        label: 'Bilaga – värmetillägg',
        type: 'attachment_ref',
        section: 'hyra',
        attachmentDefaultName: 'Värmetillägg',
        visibleWhen: { heating: 'tillagg_bilaga' },
    },
    {
        key: 'cold_water',
        label: 'Kostnad för kallvatten',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Cold water cost is included in the rent.' },
            { value: 'tillagg_bilaga', label: 'Tillägg utgår enligt bifogad klausul', code: 'tillagg_bilaga', summary: 'A cold-water supplement applies per an attached clause/annex.' },
        ],
    },
    {
        key: 'cold_water_annex',
        label: 'Bilaga – kallvattentillägg',
        type: 'attachment_ref',
        section: 'hyra',
        attachmentDefaultName: 'Kallvattentillägg',
        visibleWhen: { cold_water: 'tillagg_bilaga' },
    },
    {
        key: 'hot_water',
        label: 'Kostnad för varmvatten',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Hot water cost is included in the rent.' },
            { value: 'tillagg_bilaga', label: 'Tillägg utgår enligt bifogad klausul', code: 'tillagg_bilaga', summary: 'A hot-water supplement applies per an attached clause/annex.' },
        ],
    },
    {
        key: 'hot_water_annex',
        label: 'Bilaga – varmvattentillägg',
        type: 'attachment_ref',
        section: 'hyra',
        attachmentDefaultName: 'Varmvattentillägg',
        visibleWhen: { hot_water: 'tillagg_bilaga' },
    },
    {
        key: 'va',
        label: 'VA-kostnad',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Water & sewage (VA) cost is included in the rent.' },
            { value: 'tillagg_bilaga', label: 'VA-tillägg utgår enligt bifogad klausul', code: 'tillagg_bilaga', summary: 'A VA supplement applies per an attached clause/annex.' },
        ],
    },
    {
        key: 'va_annex',
        label: 'Bilaga – VA-tillägg',
        type: 'attachment_ref',
        section: 'hyra',
        attachmentDefaultName: 'VA-tillägg',
        visibleWhen: { va: 'tillagg_bilaga' },
    },
    {
        key: 'electricity',
        label: 'El till lägenheterna',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Electricity for the dwellings is included in the rent.' },
            { value: 'eget_abonnemang', label: 'Hyresgästen har eget abonnemang', code: 'eget_abonnemang', summary: 'The block tenant holds its own electricity subscription.' },
            { value: 'andrahands_egna', label: 'Andrahandshyresgästerna har egna abonnemang', code: 'andrahands_egna', summary: 'The sub-tenants hold their own electricity subscriptions.' },
        ],
    },
    {
        key: 'stair_cleaning',
        label: 'Trappstädning',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Stair cleaning is included in the rent.' },
            { value: 'hyresgast', label: 'Ombesörjs och bekostas av hyresgästen', code: 'hyresgast', summary: 'Stair cleaning is arranged and paid for by the tenant.' },
        ],
    },
    {
        key: 'waste_collection',
        label: 'Sophämtning',
        type: 'select',
        section: 'hyra',
        default: 'ingar',
        options: [
            { value: 'ingar', label: 'Ingår i hyran', code: 'ingar', summary: 'Waste collection is included in the rent.' },
            { value: 'hyresgast', label: 'Ombesörjs och bekostas av hyresgästen', code: 'hyresgast', summary: 'Waste collection is arranged and paid for by the tenant.' },
        ],
    },
    // §9 Indexklausul
    {
        key: 'index_clause_included',
        label: 'Indexreglering enligt bifogad indexklausul',
        type: 'boolean',
        section: 'indexklausul',
        help: 'Ändring av ovan angiven hyra sker i enlighet med bifogad indexklausul.',
    },
    {
        key: 'index_annex',
        label: 'Bilaga – indexklausul',
        type: 'attachment_ref',
        section: 'indexklausul',
        requiredAtFinal: true,
        attachmentDefaultName: 'Indexavtal',
        recommendedAttachmentTemplate: { key: 'indexavtal', version: 'v1' },
        visibleWhen: { index_clause_included: true },
    },
    // §10 Hyrans betalning
    {
        key: 'payment_period',
        label: 'Hyran betalas i förskott för varje',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'kalendermanad',
        help: 'Hyran betalas utan anfordran i förskott senast sista vardagen före varje periods början.',
        options: [
            { value: 'kalendermanad', label: 'Kalendermånad', code: 'kalendermanad', summary: 'Rent paid in advance before the start of each calendar month.' },
            { value: 'kalenderkvartal', label: 'Kalenderkvartal', code: 'kalenderkvartal', summary: 'Rent paid in advance before the start of each calendar quarter.' },
        ],
    },
    {
        key: 'payment_method',
        label: 'Betalningssätt',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'avisering',
        options: [
            { value: 'avisering', label: 'Efter avisering (rekommenderas)', code: 'avisering', summary: 'Paid after invoice/notice issued by the landlord (recommended).' },
            { value: 'plusgiro', label: 'Genom insättning på PlusGiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
            { value: 'bankgiro', label: 'Genom insättning på BankGiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
        ],
    },
    {
        key: 'payment_account',
        label: 'PlusGiro-/BankGiro-nummer',
        type: 'text',
        section: 'betalning',
        visibleWhen: { payment_method: ['plusgiro', 'bankgiro'] },
    },
    // §12 Reservnycklar
    {
        key: 'spare_key_consent',
        label: 'Hyresvärdens rätt att inneha reservnyckel',
        type: 'select',
        section: 'reservnycklar',
        requiredAtFinal: true,
        options: [
            { value: 'medger', label: 'Medger', code: 'medger', summary: 'Tenant permits the landlord to hold a spare key.' },
            { value: 'medger_inte', label: 'Medger inte', code: 'medger_inte', summary: 'Tenant does not permit the landlord to hold a spare key.' },
        ],
    },
    // §13 Underhåll m m
    {
        key: 'maintenance_mode',
        label: 'Underhållsansvar',
        type: 'select',
        section: 'underhall',
        requiredAtFinal: true,
        default: 'hyresgast_inre',
        options: [
            { value: 'hyresgast_inre', label: 'Hyresgästen ska utföra och bekosta erforderligt inre underhåll', code: 'hyresgast_inre', summary: 'Tenant performs and pays for the required interior maintenance of the dwellings.' },
            { value: 'enligt_bilaga', label: 'Hyresgästens underhållsskyldighet framgår av särskild bilaga', code: 'enligt_bilaga', summary: 'The tenant’s maintenance duty is set out in a separate annex.' },
        ],
    },
    {
        key: 'maintenance_annex',
        label: 'Bilaga – underhållsskyldighet',
        type: 'attachment_ref',
        section: 'underhall',
        requiredAtFinal: true,
        attachmentDefaultName: 'Underhållsskyldighet',
        visibleWhen: { maintenance_mode: 'enligt_bilaga' },
    },
    // §14 Besittningsskydd
    {
        key: 'possession_protection_indirect',
        label: 'Parterna avtalar om indirekt besittningsskydd (12 kap. 57–60 §§ JB)',
        type: 'boolean',
        section: 'besittningsskydd',
        default: true,
        help: 'Indirekt besittningsskydd enligt lokalhyresreglerna gäller för förlängning av detta avtal.',
    },
    // §15 Förutsättning för avtalets giltighet
    {
        key: 'requires_tribunal_approval',
        label: 'Avtalet kräver hyresnämndens tillstånd (12 kap. 1 e § JB)',
        type: 'boolean',
        section: 'forutsattning',
        help: 'Markera om avtalet för sin giltighet kräver hyresnämndens tillstånd enligt 12 kap. 1 e § jordabalken.',
    },
    // §16 Ansökan om hyresnämndens tillstånd
    {
        key: 'tenant_applies_approval',
        label: 'Blockhyresgästen ansöker om tillstånd hos hyresnämnden',
        type: 'boolean',
        section: 'ansokan',
        help: 'Hyresvärden är part i ärendet.',
    },
    // §17 Särskild anpassning
    {
        key: 'special_adaptation',
        label: 'Hyresgästen utför och bekostar särskild anpassning',
        type: 'boolean',
        section: 'sarskild-anpassning',
        help: 'Åtgärder som krävs för att objekt i blockhyreslägenheten ska uppfylla föreskrifter för aktuell verksamhet.',
    },
    // §19 Inskrivning
    {
        key: 'no_inscription',
        label: 'Hyresavtalet får inte inskrivas utan medgivande',
        type: 'boolean',
        section: 'inskrivning',
        default: true,
    },
    // §20 Hantering av personuppgifter
    {
        key: 'pdpa_info_included',
        label: 'Bifoga information om personuppgiftsbehandling',
        type: 'boolean',
        section: 'personuppgifter',
        default: true,
        help: 'Information till hyresgäst om behandling av personuppgifter bifogas som bilaga. Avmarkera för att utelämna avsnittet.',
    },
    {
        key: 'pdpa_info_annex',
        label: 'Bilaga – information om personuppgiftsbehandling',
        type: 'attachment_ref',
        section: 'personuppgifter',
        attachmentDefaultName: 'Personuppgiftsbilaga',
        recommendedAttachmentTemplate: { key: 'personuppgiftsbilaga', version: 'v1' },
        visibleWhen: { pdpa_info_included: true },
        help: 'Välj eller skapa personuppgiftsbilagan.',
    },
    // §21 Övriga bestämmelser
    {
        key: 'other_annexes',
        label: 'Tilläggsbilagor',
        type: 'data_table',
        section: 'ovriga-bestammelser',
        columns: OTHER_ANNEX_COLUMNS,
        help: 'Som tillägg till detta avtal gäller följande bilagor — ange bilagans nummer och vad den avser.',
    },
];
export const sampleValues = {
    contract_number: 'BLK-2042',
    effective_date: '2026-07-01',
    landlord_name: 'Fastighets AB Vasastaden',
    landlord_org_number: '556712-9988',
    landlord_address: 'Karlbergsvägen 12\n113 27 Stockholm',
    landlord_phone: '08-440 12 00',
    landlord_email: 'forvaltning@vasastaden.se',
    tenant_name: 'Nordtech Sverige AB',
    tenant_org_number: '556901-3344',
    tenant_address: 'Sveavägen 168\n113 46 Stockholm',
    tenant_phone: '08-700 90 00',
    tenant_email: 'facilities@nordtech.se',
    purpose_ground: 'punkt_3a',
    circle_type: 'egna_anstallda',
    block_scope_mode: 'inline_table',
    block_units: [
        { objektsnummer: 'A-1101', adress: 'Karlbergsvägen 12, lgh 1101', utrymme: 'Förråd K12', undandras: 'Ja' },
        { objektsnummer: 'A-1102', adress: 'Karlbergsvägen 12, lgh 1102', utrymme: 'Förråd K13', undandras: 'Ja' },
        { objektsnummer: 'A-1203', adress: 'Karlbergsvägen 12, lgh 1203', utrymme: '', undandras: 'Nej' },
    ],
    condition: 'befintligt',
    lease_start_date: '2026-07-01',
    lease_end_date: '2029-06-30',
    notice_months: 9,
    extension_years: 3,
    extension_months: 0,
    rent_yearly: 1440000,
    rent_type: 'total',
    heating: 'ingar',
    cold_water: 'ingar',
    hot_water: 'ingar',
    va: 'ingar',
    electricity: 'andrahands_egna',
    stair_cleaning: 'ingar',
    waste_collection: 'ingar',
    index_clause_included: true,
    payment_period: 'kalenderkvartal',
    payment_method: 'avisering',
    spare_key_consent: 'medger',
    maintenance_mode: 'hyresgast_inre',
    possession_protection_indirect: true,
    requires_tribunal_approval: true,
    tenant_applies_approval: true,
    special_adaptation: false,
    no_inscription: true,
    pdpa_info_included: true,
    other_annexes: [
        { bilaga: '4', avseende: 'Förteckning över andrahandshyresgäster' },
        { bilaga: '5', avseende: 'Skötselinstruktioner för gemensamma utrymmen' },
    ],
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
/**
 * §7 förlängningstid, fluently: "X år och Y månader", or just one when the
 * other is 0/empty. Blank mode shows both placeholders to convey the shape.
 */
function extensionPhrase(values, mode) {
    if (mode === 'blank')
        return '____ år och ____ månader';
    const years = parseNumberValue(values.extension_years);
    const months = parseNumberValue(values.extension_months);
    const parts = [];
    if (years && years > 0)
        parts.push(`${years} år`);
    if (months && months > 0)
        parts.push(`${months} månader`);
    return parts.length > 0 ? parts.join(' och ') : '____';
}
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
/** A single ☐/☑ + clause-text line for the optional-clause sections. */
function ClauseCheckbox({ checked, children, }) {
    return (_jsxs("div", { className: "flex gap-2 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "shrink-0", children: _jsx(Checkbox, { checked: checked }) }), _jsx("span", { children: children })] }));
}
/** The §4 block-units table (objektsnummer / adress / utrymme / undandras). */
function BlockUnitsTable({ values, }) {
    const rows = asDataTableRows(values.block_units);
    const isYes = (val) => typeof val === 'string' && /^(ja|j|x|☑|true)$/i.test(val.trim());
    return (_jsx("div", { className: "max-w-full overflow-x-auto", children: _jsxs("table", { className: "w-full min-w-max border-collapse text-[9px]", children: [_jsx("thead", { children: _jsx("tr", { children: BLOCK_UNIT_COLUMNS.map((c) => (_jsx("th", { className: "border border-stone-400 bg-stone-100 px-1 py-0.5 text-left font-semibold", style: { minWidth: c.width }, children: c.label }, c.key))) }) }), _jsx("tbody", { children: rows.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: BLOCK_UNIT_COLUMNS.length, className: "border border-stone-400 px-1 py-2 text-center italic text-stone-400", children: "(Inga enheter ifyllda \u00E4nnu)" }) })) : (rows.map((row, i) => (_jsxs("tr", { children: [_jsx("td", { className: "border border-stone-400 px-1 py-0.5", children: v(row.objektsnummer) }), _jsx("td", { className: "border border-stone-400 px-1 py-0.5", children: v(row.adress) }), _jsx("td", { className: "border border-stone-400 px-1 py-0.5", children: v(row.utrymme) }), _jsx("td", { className: "border border-stone-400 px-1 py-0.5 text-center", children: _jsx(Checkbox, { checked: isYes(row.undandras ?? null) }) })] }, i)))) })] }) }));
}
/** The §21 tilläggsbilagor table (Bilaga | Avseende). */
function OtherAnnexesTable({ values, }) {
    const rows = asDataTableRows(values.other_annexes);
    if (rows.length === 0) {
        return (_jsx("p", { className: "m-0 text-[10px] italic text-stone-500", children: "(Inga till\u00E4ggsbilagor angivna)" }));
    }
    return (_jsx("ul", { className: "m-0 flex list-none flex-col gap-1 p-0 text-[10.5px] leading-relaxed", children: rows.map((row, i) => (_jsxs("li", { className: "flex gap-2", children: [_jsx("span", { className: "shrink-0", children: _jsx(Checkbox, { checked: true }) }), _jsxs("span", { children: ["Bilaga ", v(row.bilaga), " avseende ", v(row.avseende)] })] }, i))) }));
}
export function Body({ values, mode, attachments, signatories, }) {
    const is3a = cond(mode, values.purpose_ground !== 'punkt_3b');
    const is3b = cond(mode, values.purpose_ground === 'punkt_3b');
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsxs("div", { className: "mb-2 flex flex-wrap gap-x-8 gap-y-1 text-[10px] text-stone-700", children: [_jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Kontraktsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.contract_number) })] }), _jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Avtalets ikrafttr\u00E4dande:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.effective_date) })] })] }), _jsx("p", { className: "mb-4 text-[10px] italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsx(Section, { sectionRef: "blockhyresgast", title: "Blockhyresg\u00E4st", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessee", values: values }) })] }), _jsxs(Section, { sectionRef: "upplatelseandamal", title: "Uppl\u00E5telse\u00E4ndam\u00E5l: f\u00F6retagsbost\u00E4der", children: [is3a && (_jsxs("div", { className: "mb-2", children: [_jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: PURPOSE_GROUND_3A_BODY }), _jsxs("div", { className: "mt-1", children: [_jsx(FieldRow, { label: "Kretsen utg\u00F6rs av", children: values.circle_type === 'annan_krets'
                                            ? 'Annan krets, specificerad nedan'
                                            : values.circle_type === 'egna_anstallda'
                                                ? 'Egna anställda'
                                                : v(undefined) }), cond(mode, values.circle_type === 'annan_krets') && (_jsx(FieldRow, { label: "Avgr\u00E4nsningskriterium", children: v(values.circle_specification) }))] })] })), is3b && (_jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: PURPOSE_GROUND_3B_BODY }))] }), _jsxs(Section, { sectionRef: "omfattning", title: "Blockl\u00E4genhetens omfattning", children: [cond(mode, values.block_scope_mode !== 'per_annex') && (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-2 text-[10px] text-stone-600", children: "I blockl\u00E4genheten ing\u00E5r f\u00F6ljande enheter:" }), _jsx(BlockUnitsTable, { values: values })] })), cond(mode, values.block_scope_mode === 'per_annex') && (_jsx(FieldRow, { label: "Blockl\u00E4genhetens omfattning framg\u00E5r av", children: attachmentLabel(attachments, values.block_scope_annex) }))] }), _jsxs(Section, { sectionRef: "skick", title: "Blockhyresl\u00E4genhetens skick", children: [_jsx(FieldRow, { label: "Skick", children: values.condition === 'avtalat_enligt_bilaga'
                            ? 'Avtalat skick enligt bilaga'
                            : values.condition === 'befintligt'
                                ? 'Befintligt skick'
                                : v(undefined) }), cond(mode, values.condition === 'avtalat_enligt_bilaga') && (_jsx(FieldRow, { label: "Bilaga \u2013 avtalat skick", children: attachmentLabel(attachments, values.condition_annex) }))] }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs(Section, { sectionRef: "hyrestid", title: "Hyrestid", children: [_jsx(FieldRow, { label: "Fr\u00E5n och med", children: v(values.lease_start_date) }), _jsx(FieldRow, { label: "Till och med", children: v(values.lease_end_date) })] }), _jsx(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gning", children: _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: ["Upps\u00E4gning av detta hyresavtal ska ske skriftligen minst ", num(values.notice_months), " m\u00E5nader f\u00F6re den avtalade hyrestidens utg\u00E5ng. I annat fall \u00E4r kontraktet f\u00F6rl\u00E4ngt med", ' ', extensionPhrase(values, mode), " f\u00F6r varje g\u00E5ng."] }) })] }), _jsxs(Section, { sectionRef: "hyra", title: "Hyra", children: [_jsx(FieldRow, { label: "Kronor per \u00E5r", children: num(values.rent_yearly) }), _jsx(FieldRow, { label: "Hyran utg\u00F6r", children: values.rent_type === 'exkl_tillagg'
                            ? 'Hyra exkl. nedan markerade tillägg'
                            : values.rent_type === 'total'
                                ? 'Total hyra'
                                : v(undefined) }), _jsx("div", { className: "mt-2 mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "Mediatyp / betalning" }), _jsxs(FieldRow, { label: "Kostnad f\u00F6r v\u00E4rme", children: [v(mediaLabel('heating', values.heating)), cond(mode, values.heating === 'tillagg_bilaga') && (_jsxs(_Fragment, { children: [" (Bilaga: ", attachmentLabel(attachments, values.heating_annex), ")"] }))] }), _jsxs(FieldRow, { label: "Kostnad f\u00F6r kallvatten", children: [v(mediaLabel('cold_water', values.cold_water)), cond(mode, values.cold_water === 'tillagg_bilaga') && (_jsxs(_Fragment, { children: [" (Bilaga: ", attachmentLabel(attachments, values.cold_water_annex), ")"] }))] }), _jsxs(FieldRow, { label: "Kostnad f\u00F6r varmvatten", children: [v(mediaLabel('hot_water', values.hot_water)), cond(mode, values.hot_water === 'tillagg_bilaga') && (_jsxs(_Fragment, { children: [" (Bilaga: ", attachmentLabel(attachments, values.hot_water_annex), ")"] }))] }), _jsxs(FieldRow, { label: "VA-kostnad", children: [v(mediaLabel('va', values.va)), cond(mode, values.va === 'tillagg_bilaga') && (_jsxs(_Fragment, { children: [" (Bilaga: ", attachmentLabel(attachments, values.va_annex), ")"] }))] }), _jsx(FieldRow, { label: "El till l\u00E4genheterna", children: v(mediaLabel('electricity', values.electricity)) }), _jsx(FieldRow, { label: "Trappst\u00E4dning", children: v(mediaLabel('stair_cleaning', values.stair_cleaning)) }), _jsx(FieldRow, { label: "Soph\u00E4mtning", children: v(mediaLabel('waste_collection', values.waste_collection)) })] }), _jsx(Section, { sectionRef: "indexklausul", title: "Indexklausul", children: _jsxs("p", { className: "m-0 text-[10.5px] leading-relaxed", children: ["\u00C4ndring av ovan angiven hyra sker i enlighet med bifogad indexklausul. Bilaga:", ' ', attachmentLabel(attachments, values.index_annex)] }) }), _jsxs(Section, { sectionRef: "betalning", title: "Hyrans betalning", children: [_jsxs("p", { className: "mb-1 text-[10.5px] leading-relaxed", children: ["Hyran betalas utan anfordran i f\u00F6rskott senast sista vardagen f\u00F6re varje", ' ', values.payment_period === 'kalenderkvartal' ? 'kalenderkvartals' : 'kalendermånads', " b\u00F6rjan."] }), _jsx(FieldRow, { label: "Betalningss\u00E4tt", children: v(PAYMENT_METHOD_LABELS[values.payment_method ?? ''] ?? values.payment_method) }), cond(mode, values.payment_method === 'plusgiro' || values.payment_method === 'bankgiro') && (_jsx(FieldRow, { label: "PlusGiro-/BankGiro-nummer", children: v(values.payment_account) }))] }), _jsx(Section, { sectionRef: "ranta", title: "R\u00E4nta, betalningsp\u00E5minnelse", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Vid f\u00F6rsenad hyresbetalning ska hyresg\u00E4sten betala dels r\u00E4nta enligt r\u00E4ntelagen, dels ers\u00E4ttning f\u00F6r skriftlig betalningsp\u00E5minnelse enligt lagstiftningen om ers\u00E4ttning f\u00F6r inkassokostnader m.m." }) }), _jsx(Section, { sectionRef: "reservnycklar", title: "Reservnycklar", children: _jsxs("p", { className: "m-0 text-[10.5px] leading-relaxed", children: ["Hyresg\u00E4sten", ' ', values.spare_key_consent === 'medger_inte'
                            ? 'medger inte'
                            : values.spare_key_consent === 'medger'
                                ? 'medger'
                                : '____________', ' ', "hyresv\u00E4rden r\u00E4tt att inneha reservnyckel till l\u00E4genheten."] }) }), _jsxs(Section, { sectionRef: "underhall", title: "Underh\u00E5ll m m", children: [_jsx(ClauseCheckbox, { checked: cond(mode, values.maintenance_mode !== 'enligt_bilaga'), children: "Hyresg\u00E4sten ska utf\u00F6ra och bekosta erforderligt inre underh\u00E5ll av blockhyresl\u00E4genheten. \u00C5tagandet omfattar s\u00E5ledes s\u00E5v\u00E4l ytskikt som tillh\u00F6rande inredning." }), _jsx("div", { className: "mt-1", children: _jsxs(ClauseCheckbox, { checked: cond(mode, values.maintenance_mode === 'enligt_bilaga'), children: ["Hyresg\u00E4stens underh\u00E5llsskyldighet framg\u00E5r av s\u00E4rskild bilaga.", cond(mode, values.maintenance_mode === 'enligt_bilaga') && (_jsxs(_Fragment, { children: [" Bilaga: ", attachmentLabel(attachments, values.maintenance_annex)] }))] }) })] }), _jsx(Section, { sectionRef: "besittningsskydd", title: "Besittningsskydd f\u00F6r blockhyresg\u00E4sten", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Parterna avtalar om indirekt besittningsskydd enligt lokalhyresreglerna (12 kap. 57\u201360 \u00A7\u00A7 JB) g\u00E4ller f\u00F6r f\u00F6rl\u00E4ngning av detta avtal." }) }), _jsx(Section, { sectionRef: "forutsattning", title: "F\u00F6ruts\u00E4ttning f\u00F6r avtalets giltighet", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Avtalet kr\u00E4ver f\u00F6r sin giltighet hyresn\u00E4mndens tillst\u00E5nd enligt 12 kap. 1 e \u00A7 jordabalken." }) }), _jsx(Section, { sectionRef: "ansokan", title: "Ans\u00F6kan om hyresn\u00E4mndens tillst\u00E5nd", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Blockhyresg\u00E4sten ska ans\u00F6ka om tillst\u00E5nd hos hyresn\u00E4mnden. Hyresv\u00E4rden \u00E4r part i \u00E4rendet." }) }), _jsx(Section, { sectionRef: "sarskild-anpassning", title: "S\u00E4rskild anpassning", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten utf\u00F6r och bekostar de \u00E5tg\u00E4rder som kr\u00E4vs f\u00F6r att objekt i blockhyresl\u00E4genheten vid tilltr\u00E4det eller senare ska uppfylla de f\u00F6reskrifter som g\u00E4ller f\u00F6r aktuell verksamhet." }) }), _jsxs(Section, { sectionRef: "allmanna-villkor", title: "Allm\u00E4nna villkor", children: [_jsx("div", { className: "mb-1 text-[11px] font-bold", children: "Neds\u00E4ttning av hyran vid sedvanligt underh\u00E5ll" }), _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten har inte r\u00E4tt att f\u00E5 neds\u00E4ttning av hyran f\u00F6r tid, under vilken hyresv\u00E4rden l\u00E5ter verkst\u00E4lla arbete f\u00F6r sedvanligt underh\u00E5ll av l\u00E4genheterna eller fastigheten. Arbetet ska dock utf\u00F6ras utan on\u00F6dig tidsutdr\u00E4kt." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Best\u00E4mmelserna i 12 kap. JB om att hyresg\u00E4sten ska v\u00E4l v\u00E5rda l\u00E4genheten och vad som h\u00F6r till dessa samt vid l\u00E4genhetens anv\u00E4ndning se till att g\u00F6ra allt som kr\u00E4vs f\u00F6r att bevara sundhet, ordning och gott skick inom fastigheten avser blockhyresl\u00E4genheten och samtliga objekt som ing\u00E5r i den." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten f\u00F6rbinder sig" }), _jsxs("ul", { className: "mt-1 list-none space-y-1 pl-0 text-[10.5px] leading-relaxed", children: [_jsx("li", { children: "att inte utan s\u00E4rskilt tillst\u00E5nd s\u00E4tta upp anslag, skyltar, markiser, utomhusantenner eller annat liknande p\u00E5 fastigheten samt att vid reparation av fastigheten p\u00E5 egen bekostnad ta ner och i f\u00F6rekommande fall s\u00E4tta upp dem igen; meddelanden kan dock s\u00E4ttas upp p\u00E5 anslagstavla som \u00E4r avsedd f\u00F6r s\u00E5dana." }), _jsx("li", { children: "att p\u00E5 egen bekostnad l\u00E5ta forsla bort f\u00F6r sopnedkast eller soptunna ol\u00E4mpligt avfall om inte hyresv\u00E4rden s\u00E4rskilt tillhandah\u00E5ller container eller liknande" }), _jsx("li", { children: "att h\u00E5lla till l\u00E4genheterna h\u00F6rande balkonger eller altaner fria fr\u00E5n sn\u00F6 och is" }), _jsx("li", { children: "att i fastighet d\u00E4r trappst\u00E4dning inte ing\u00E5r i hyran i tur med \u00F6vriga hyresg\u00E4ster ombes\u00F6rja trappst\u00E4dningen" }), _jsx("li", { children: "att h\u00E5lla l\u00E4genheterna tillg\u00E4ngliga f\u00F6r sotning och rensning av kanaler" }), _jsx("li", { children: "att vid avflyttning l\u00E4mna l\u00E4genheterna v\u00E4l rengjorda samt att d\u00E5 till hyresv\u00E4rden \u00F6verl\u00E4mna samtliga port- och d\u00F6rrnycklar, \u00E4ven om de har anskaffats av hyresg\u00E4sten." })] }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4stens \u00E5tagande enligt ovan inneb\u00E4r ocks\u00E5 en skyldighet att verka f\u00F6r att andrahandshyresg\u00E4sterna i f\u00F6rekommande fall i sin tur agerar i enlighet med \u00E5tagandet." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Hyresv\u00E4rden fritar sig fr\u00E5n skyldighet att fullg\u00F6ra sin del av avtalet och fr\u00E5n skyldighet att betala skadest\u00E5nd om hans eller hennes \u00E5taganden inte alls eller endast till onormalt h\u00F6g kostnad kan fullg\u00F6ras p\u00E5 grund av krig eller upplopp eller p\u00E5 grund av s\u00E5dan arbetsinst\u00E4llelse, blockad, eldsv\u00E5da, explosion eller ingrepp av offentlig myndighet som hyresv\u00E4rden inte r\u00E5der \u00F6ver och inte heller har kunnat f\u00F6rutse." })] }), _jsx(Section, { sectionRef: "inskrivning", title: "Inskrivning", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed", children: "Hyresavtalet f\u00E5r inte inskrivas utan s\u00E4rskilt medgivande fr\u00E5n hyresv\u00E4rden." }) }), _jsx(Section, { sectionRef: "personuppgifter", title: "Hantering av personuppgifter", children: _jsxs("p", { className: "m-0 text-[10.5px] leading-relaxed", children: ["Information till hyresg\u00E4st om behandling av personuppgifter, se Bilaga:", ' ', attachmentLabel(attachments, values.pdpa_info_annex)] }) }), _jsxs(Section, { sectionRef: "ovriga-bestammelser", title: "\u00D6vriga best\u00E4mmelser", children: [_jsx("p", { className: "mb-2 text-[10.5px] leading-relaxed", children: "Som till\u00E4gg till detta avtal g\u00E4ller f\u00F6ljande bilagor:" }), _jsx(OtherAnnexesTable, { values: values })] }), _jsxs("div", { className: "mt-4", style: { breakInside: 'avoid' }, children: [_jsx("h2", { className: "mt-1.5 mb-1.5 border-b border-stone-300 pb-1 text-[12px] font-bold", children: "Bilagor" }), _jsx(AttachmentList, { attachments: attachments })] }), _jsx("div", { className: "mt-4", style: { breakInside: 'avoid' }, children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                        ], signatories: signatories }) }) })] }));
}
/** Display labels for the §8 media selects (value code → Swedish label). */
const MEDIA_LABELS = {
    heating: { ingar: 'Ingår i hyran', tillagg_bilaga: 'Värmetillägg utgår enligt bifogad klausul' },
    cold_water: { ingar: 'Ingår i hyran', tillagg_bilaga: 'Tillägg utgår enligt bifogad klausul' },
    hot_water: { ingar: 'Ingår i hyran', tillagg_bilaga: 'Tillägg utgår enligt bifogad klausul' },
    va: { ingar: 'Ingår i hyran', tillagg_bilaga: 'VA-tillägg utgår enligt bifogad klausul' },
    electricity: {
        ingar: 'Ingår i hyran',
        eget_abonnemang: 'Hyresgästen har eget abonnemang',
        andrahands_egna: 'Andrahandshyresgästerna har egna abonnemang',
    },
    stair_cleaning: { ingar: 'Ingår i hyran', hyresgast: 'Ombesörjs och bekostas av hyresgästen' },
    waste_collection: { ingar: 'Ingår i hyran', hyresgast: 'Ombesörjs och bekostas av hyresgästen' },
};
/** Resolve a §8 media-row label; falls back to the raw code if unmapped. */
const mediaLabel = (group, code) => {
    const map = MEDIA_LABELS[group];
    const fromMap = code ? map?.[code] : undefined;
    return fromMap ?? code;
};
const PAYMENT_METHOD_LABELS = {
    avisering: 'Efter avisering (rekommenderas)',
    plusgiro: 'Genom insättning på PlusGiro',
    bankgiro: 'Genom insättning på BankGiro',
};
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map